import { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { Popover, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import PlusIconSmall from 'packages/kakapo-ui-kit/assets/icons/PlusIconSmall';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import FieldDummyAction from 'packages/kakapo-ui-kit/components/forms/controls/FieldDummyAction';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import FeedbackComposerCompetencies from './FeedbackComposerCompetencies';
import FeedbackComposerGoals from './FeedbackComposerGoals';
import FeedbackComposerQuestion from './FeedbackComposerQuestion';

import { QuestionSelector } from 'feedback/settings/AddFeedbackQuestion';

export default function FeedbackComposer(props: {
	method: 'template' | 'custom' | 'competencies' | 'goals';
	userID: string;
	autoFocus?: boolean;
	questions: (graphql.FeedbackRequestQuestionInput & { id: string })[];
	goals: { goalID: string; text?: string; order: number }[];
	competencies: { competencyID: string; text?: string }[];
	onChange: (
		questions: (graphql.FeedbackRequestQuestionInput & { id: string })[],
		goals: { goalID: string; text?: string; order: number }[],
		competencies: { competencyID: string; text?: string }[],
	) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [library, setLibrary] = useState(false);

	const [selectedQuestion, setSelectedQuestion] = useState<string | undefined>(
		props.autoFocus ? props.questions[0]?.id : undefined,
	);

	return (
		<>
			{props.method !== 'goals' && props.method !== 'competencies' ? (
				<>
					<Box mb={3}>
						<Box pt={3} pb={2}>
							<Subheading>{t('feedback.composer.questions.title')}</Subheading>
						</Box>
						<HorizontalRule />
					</Box>
					<DragDropContext
						onDragEnd={(change) => {
							if (!change.destination) {
								return;
							}

							const questions = props.questions.slice();
							const [item] = questions.splice(change.source.index, 1);
							questions.splice(change.destination!.index, 0, item);
							props.onChange(questions, props.goals, props.competencies);
						}}
					>
						<Droppable
							droppableId="droppable"
							type="FEEDBACK"
							renderClone={(provided, snapshot, rubric) => {
								return (
									<Box ref={provided.innerRef} {...provided.draggableProps} paddingBottom={1}>
										<FeedbackComposerQuestion
											selected={selectedQuestion === props.questions[rubric.source.index].id}
											onFocus={() => {}}
											provided={provided}
											index={Math.min(rubric.source.index, props.questions.length - 1)}
											question={props.questions[rubric.source.index]}
											onChange={() => {}}
											onRemove={() => {}}
										/>
									</Box>
								);
							}}
						>
							{(provided) => (
								<Box ref={provided.innerRef} {...provided.droppableProps}>
									{props.questions.map((question, index) => (
										<Draggable key={question.id} draggableId={question.id} index={index}>
											{(provided) => (
												<Box ref={provided.innerRef} {...provided.draggableProps} paddingBottom={1}>
													<FeedbackComposerQuestion
														provided={provided}
														index={index}
														question={question}
														selected={selectedQuestion === question.id}
														onFocus={() => setSelectedQuestion(question.id)}
														onChange={(question) => {
															const questions = props.questions.slice();
															questions[index] = { ...question, id: questions[index].id };
															props.onChange(questions, props.goals, props.competencies);
														}}
														onRemove={() => {
															const questions = props.questions.slice();
															questions.splice(index, 1);
															props.onChange(questions, props.goals, props.competencies);
														}}
													/>
												</Box>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</Box>
							)}
						</Droppable>
					</DragDropContext>
					<Box mb={3}>
						<Box pt={3} pb={2}>
							<Subheading>{t('feedback.composer.questions.add')}</Subheading>
						</Box>
						<HorizontalRule />
					</Box>
					<Flex>
						<Box flexBasis={0} flexGrow={1} flexShrink={1} mr={2}>
							<FieldDummyAction
								title={t('feedback.composer.addQuery')}
								icon={<PlusIconSmall />}
								onClick={() => {
									const id = Math.random().toString();
									const questions = props.questions.slice();
									questions.push({
										id,
										text: '',
										description: { ops: [] },
										type: graphql.FeedbackQuestionType.Text,
									});
									props.onChange(questions, props.goals, props.competencies);
									setSelectedQuestion(id);
								}}
								fullWidth
							/>
						</Box>
						<Box flexBasis={0} flexGrow={1} flexShrink={1} ml={2}>
							<Popover.Target
								visible={library}
								center
								contents={
									<QuestionSelector
										readOnly
										onCancel={() => setLibrary(false)}
										onSelect={(selection) => {
											const questions = props.questions.slice();

											if (questions.length === 1 && questions[0].text.trim().length === 0) {
												questions.splice(0, questions.length);
											}

											for (const question of selection) {
												const id = Math.random().toString();
												questions.push({
													id,
													text: localize(question.text).text,
													description: { ops: [] },
													type: question.type,
												});
											}
											props.onChange(questions, props.goals, props.competencies);
											setSelectedQuestion(undefined);
											setLibrary(false);
										}}
									/>
								}
							>
								<FieldDummyAction
									title={t('feedback.composer.selectQuery')}
									icon={<PlusIconSmall />}
									onClick={() => setLibrary(true)}
									fullWidth
								/>
							</Popover.Target>
						</Box>
					</Flex>
				</>
			) : (
				<>
					{props.method === 'competencies' && (
						<Box mb={4}>
							<FeedbackComposerCompetencies
								userID={props.userID}
								competencies={props.competencies}
								onChange={(competencies) =>
									props.onChange(props.questions, props.goals, competencies)
								}
							/>
						</Box>
					)}
					{props.method === 'goals' && (
						<Box mb={4}>
							<FeedbackComposerGoals
								userID={props.userID}
								goals={props.goals}
								onChange={(goals) => props.onChange(props.questions, goals, props.competencies)}
							/>
						</Box>
					)}
				</>
			)}
		</>
	);
}
